import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { buildMeta } from "../../buildMeta";
import "./VersionUpdatePill.css";

/*
 * Tells a long-lived tab that a newer deployment exists.
 *
 * An open tab keeps running the JavaScript it booted with, so after a deploy it
 * quietly serves the old version until something reloads it. Nothing is broken,
 * which is why RouteErrorBoundary stays quiet -- that catches a navigation that
 * actually failed. This is the other half: nothing is broken yet, but the tab
 * is behind, and during a test round that means reports filed against a version
 * nobody is looking at any more.
 *
 * version.json is written by writeBuildMeta on every build and served from the
 * deployment root. buildMeta is the same data compiled into this bundle, so
 * comparing the two is the whole check.
 *
 * It previously compared asset-manifest.json's main bundle filename. That is a
 * create-react-app artifact -- Vite does not emit one -- and this component
 * fails closed, so a bundler change would have silently stopped it noticing
 * deploys. version.json is ours and survives that.
 */

const POLL_MS = 60000;
const VERSION_URL = "/version.json";
const DISMISSED_KEY = "ctb-update-dismissed";

async function fetchDeployed() {
  try {
    /*
     * "no-cache" rather than "no-store": both guarantee a fresh answer, but
     * this one revalidates against the ETag, so a repeat check costs a 304.
     * That matters because navigation triggers a check.
     */
    const res = await fetch(VERSION_URL, { cache: "no-cache" });

    if (!res.ok) return null;

    /*
     * Throws when the file is missing and the SPA rewrite answers with
     * index.html instead, which is also what the dev server does. Either way
     * there is nothing to compare, so it is not worth surfacing.
     */
    const deployed = await res.json();

    return deployed && typeof deployed.commit === "string" ? deployed : null;
  } catch {
    return null;
  }
}

function readDismissed() {
  try {
    return sessionStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
}

export default function VersionUpdatePill() {
  const [deployed, setDeployed] = useState(null);
  const [dismissed, setDismissed] = useState(readDismissed);
  const { pathname } = useLocation();

  const check = useCallback(async () => {
    /* Nothing to compare against without a build stamp, e.g. the dev server. */
    if (!buildMeta.commit) return;

    const next = await fetchDeployed();

    /*
     * Keyed on the commit, not the version: a deploy does not always bump the
     * version, and every deploy is worth noticing. The version is for display.
     */
    if (!next || next.commit === buildMeta.commit) return;

    setDeployed(next);
  }, []);

  /*
   * On every navigation, and on mount, since pathname is set from the first
   * render. Clicking through the site is when a stale tab actually matters --
   * it is the same moment a rehashed chunk would 404 and trip
   * RouteErrorBoundary -- so this is where the notice earns its keep. A
   * 60 second timer alone can easily be beaten by someone browsing.
   */
  useEffect(() => {
    check();
  }, [pathname, check]);

  useEffect(() => {
    if (!buildMeta.commit) return undefined;

    const timer = setInterval(check, POLL_MS);

    /* Returning to a tab is when a stale one is most likely to be used again,
     * and it costs one revalidation. */
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") check();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [check]);

  /* Dismissal is keyed to the commit dismissed, so saying no once does not
   * silence every future release. */
  if (!deployed || dismissed === deployed.commit) return null;

  /*
   * Name the version when it actually changed. Deploys that do not bump it --
   * a content fix, a copy change -- would otherwise announce the number the
   * reader is already on, which reads like a bug.
   */
  const versionChanged =
    deployed.version && deployed.version !== buildMeta.version;

  return (
    <div
      className="ctb-updatePill"
      data-testid="version-update-pill"
      role="status"
      aria-live="polite"
    >
      <span className="ctb-updatePillCopy">
        {versionChanged ? (
          <>
            Version <strong>{deployed.version}</strong> is available
          </>
        ) : (
          "An update is available"
        )}
      </span>

      <button
        type="button"
        className="ctb-updatePillRefresh"
        onClick={() => window.location.reload()}
      >
        Refresh
      </button>

      <button
        type="button"
        className="ctb-updatePillDismiss"
        aria-label="Dismiss the update notice"
        onClick={() => {
          try {
            sessionStorage.setItem(DISMISSED_KEY, deployed.commit);
          } catch {
            /* Dismissal lasts for this render either way. */
          }

          setDismissed(deployed.commit);
        }}
      >
        ×
      </button>
    </div>
  );
}
